import React, { useMemo, useState } from "react";
import { UserPlus, Pencil, Trash2, X, BarChart3 } from "lucide-react";
import { Panel } from "../components/primitives";
import { useToast } from "../components/Toast";
import { useActiveTeam } from "../components/ActiveTeam";
import { useVersion } from "../components/Version";
import PlayerProfile from "../components/PlayerProfile";
import MembersMobile from "./MembersMobile";
import { api } from "../api";
import { canDo } from "../data";

// MEMBER was removed everywhere in favor of PLAYER as the default catch-all.
const ALL_ROLES = [
  "PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD",
  "VOLLEYBALL_HEAD","CAPTAIN","PLAYER","ADMIN"
];

// Display labels are mostly the role code with underscores → spaces; a couple
// have explicit overrides for readability.
const ROLE_LABEL = {
  PRESIDENT:       "President",
  VICE_PRESIDENT:  "Vice President",
  FINANCE_HEAD:    "Finance Head",
  CRICKET_HEAD:    "Cricket Head",
  VOLLEYBALL_HEAD: "Volleyball Head",
  CAPTAIN:         "Captain",
  PLAYER:          "Player",
  ADMIN:           "Admin"
};
function roleLabel(role) { return ROLE_LABEL[role] || role.replace("_", " "); }

// Strip any legacy MEMBER assignment from a role list; if that leaves nothing,
// fall back to PLAYER so the user always has a meaningful role.
function normalizeRoles(roles) {
  const filtered = (roles || []).filter(r => r !== "MEMBER");
  return filtered.length ? filtered : ["PLAYER"];
}

const EMPTY_FORM = {
  firstName: "", lastName: "", email: "", phone: "",
  password: "gabruz123", roles: ["PLAYER"], teamIds: []
};

export default function Members({ state, reload, user }) {
  const { version } = useVersion();
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  // Phase 3 — cricket player profile modal. profileFor holds the player
  // + cricket team context, or null when closed.
  const [profileFor, setProfileFor] = useState(null);

  const canCreate = canDo(user, "members", "create");
  const canUpdate = canDo(user, "members", "update");
  const canDelete = canDo(user, "members", "delete");

  // Phase 3 — scope to active team unless leadership has picked "All Teams".
  const sortedMembers = useMemo(() => {
    let pool = state.members;
    if (!isAllTeams) {
      const ids = new Set(activeTeam?.players || []);
      pool = pool.filter(m => ids.has(m.id));
    }
    return [...pool].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
  }, [state.members, activeTeam, isAllTeams]);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(""); setOk("");
  }

  function toggleInList(field, value) {
    setForm(prev => {
      const list = prev[field];
      return {
        ...prev,
        [field]: list.includes(value) ? list.filter(x => x !== value) : [...list, value]
      };
    });
  }

  function openCreate() {
    setEditingId(null);
    // Pre-select the active team so new members default onto whichever team
    // the page is currently scoped to.
    setForm({ ...EMPTY_FORM, teamIds: activeTeam ? [activeTeam.id] : [] });
    setShowForm(true);
    setError(""); setOk("");
  }

  function openEdit(m) {
    setEditingId(m.id);
    const memberTeams = state.teams.filter(t => t.players?.includes(m.id)).map(t => t.id);
    setForm({
      firstName: m.firstName || "",
      lastName:  m.lastName  || "",
      email:     m.email     || "",
      phone:     m.phone     || "",
      password:  "",   // not edited from this UI
      roles:     normalizeRoles(m.roles),
      teamIds:   memberTeams
    });
    setShowForm(true);
    setError(""); setOk("");
  }

  function close() {
    setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); setError(""); setOk("");
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setOk("");

    if (!form.email.trim() || !form.firstName.trim() || !form.phone.trim()) {
      setError("First name, email, and phone are required.");
      return;
    }
    if (form.roles.length === 0) {
      setError("Pick at least one role.");
      return;
    }

    setBusy(true);
    try {
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      let userId;

      if (editingId) {
        // Update the existing member
        await api.updateMember(editingId, {
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim() || null,
          name:      fullName,
          phone:     form.phone.trim(),
          roles:     form.roles
        });
        userId = editingId;
      } else {
        const r = await api.createMember({
          email:     form.email.trim(),
          password:  form.password || "gabruz123",
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim() || null,
          name:      fullName,
          phone:     form.phone.trim(),
          roles:     form.roles
        });
        userId = r.id;
      }

      // Reconcile team membership — diff against current memberships.
      const currentMemberships = state.teams.filter(t => t.players?.includes(userId)).map(t => t.id);
      const toAdd    = form.teamIds.filter(tid => !currentMemberships.includes(tid));
      const toRemove = currentMemberships.filter(tid => !form.teamIds.includes(tid));

      for (const teamId of toAdd) {
        const team = state.teams.find(t => t.id === teamId);
        const players = [...(team?.players || []), userId];
        await api.updateTeam(teamId, { players });
      }
      for (const teamId of toRemove) {
        const team = state.teams.find(t => t.id === teamId);
        const players = (team?.players || []).filter(id => id !== userId);
        await api.updateTeam(teamId, { players });
      }

      setOk(editingId ? `Updated ${fullName}.` : `Added ${fullName}.`);
      close();
      await reload();
    } catch (err) {
      setError(err.message || "Failed to save member.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(m) {
    if (!confirm(`Deactivate member "${m.name}"? This soft-deletes them — they can be reactivated from the database.`)) return;
    try {
      await api.deleteMember(m.id);
      await reload();
    } catch (err) {
      toast.error("Delete failed: " + err.message);
    }
  }

  // Reusable form panel — shared between desktop and V4 branches.
  const formPanel = showForm && (canCreate || canUpdate) && (
    <Panel title={editingId ? "Edit Member" : "Add New Member"}>
      <form className="form" onSubmit={submit}>
        <div className="two">
          <div>
            <label>First Name *</label>
            <input value={form.firstName} onChange={e => update("firstName", e.target.value)} placeholder="Ashish" />
          </div>
          <div>
            <label>Last Name</label>
            <input value={form.lastName} onChange={e => update("lastName", e.target.value)} placeholder="Bhat" />
          </div>
        </div>
        <div className="two">
          <div>
            <label>Email *</label>
            <input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="name@gabruzuniverse.com" disabled={!!editingId} />
          </div>
          <div>
            <label>Phone *</label>
            <input value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="412-555-0000" />
          </div>
        </div>
        {!editingId && (
          <div>
            <label>Initial Password</label>
            <input value={form.password} onChange={e => update("password", e.target.value)} placeholder="gabruz123" />
            <small style={{ display: "block", color: "var(--muted)", marginTop: 4 }}>
              Defaults to <code>gabruz123</code> if left blank. User can change it after first login.
            </small>
          </div>
        )}
        <div>
          <label>Roles *</label>
          <div className="players">
            {ALL_ROLES.map(role => (
              <label key={role}>
                <input type="checkbox" checked={form.roles.includes(role)} onChange={() => toggleInList("roles", role)} />
                <span>{roleLabel(role)}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label>Team Assignment</label>
          <div className="players">
            {state.teams.length === 0 && (
              <small style={{ color: "var(--muted)", gridColumn: "1 / -1" }}>No teams exist yet.</small>
            )}
            {state.teams.map(t => (
              <label key={t.id}>
                <input type="checkbox" checked={form.teamIds.includes(t.id)} onChange={() => toggleInList("teamIds", t.id)} />
                <span>{t.name} ({t.sport})</span>
                <em>{t.players?.length || 0}</em>
              </label>
            ))}
          </div>
          <small style={{ display: "block", color: "var(--muted)", marginTop: 6 }}>
            Pick the teams this member should belong to. Edits add/remove them on save.
          </small>
        </div>
        {error && <div className="err">{error}</div>}
        {ok    && <div className="note">{ok}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Saving…" : (editingId
              ? <>Save Changes</>
              : <><UserPlus size={16}/> Add Member</>)}
          </button>
        </div>
      </form>
    </Panel>
  );

  // V4 — branch into the mobile card list. Reuses the same form +
  // PlayerProfile modal flow.
  if (version === "v4") {
    return (
      <div className="stack">
        {formPanel}
        <MembersMobile
          state={state}
          reload={reload}
          user={user}
          openCreate={openCreate}
          openEdit={openEdit}
        />
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="builderTop">
        <div>
          <p>Roster · Community · Leadership</p>
          <h2>Members &amp; Leadership</h2>
        </div>
        <div>
          {canCreate && !showForm && (
            <button className="btn" onClick={openCreate}>
              <UserPlus size={16}/> Add Member
            </button>
          )}
          {showForm && (
            <button className="dark" onClick={close}>
              <X size={16}/> Close
            </button>
          )}
        </div>
      </div>

      {formPanel}

      <Panel title={isAllTeams ? "All Members" : `${activeTeam?.name || "Team"} Members`}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Roles</th>
              <th>Teams</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map(u => {
              const teams = state.teams.filter(t => t.players?.includes(u.id));
              const roles = (u.roles || []).filter(r => r !== "MEMBER");
              // Phase 3 — cricket profile button only shows for players
              // who sit on at least one cricket team. If the active team
              // is itself a cricket team, prefer that as the profile
              // scope; otherwise fall back to their first cricket team.
              const cricketTeams = teams.filter(t => t.sport === "Cricket");
              const profileTeam = !isAllTeams && activeTeam?.sport === "Cricket"
                ? activeTeam
                : cricketTeams[0];
              return (
                <tr key={u.id}>
                  <td><b>{u.name}</b></td>
                  <td>{u.email}</td>
                  <td>{u.phone || "—"}</td>
                  <td>
                    {roles.length === 0
                      ? <span style={{color:"var(--muted)"}}>—</span>
                      : (
                        <div className="tagRow">
                          {roles.map(r => (
                            <span key={r} className={`roleTag roleTag--${r}`}>{roleLabel(r)}</span>
                          ))}
                        </div>
                      )}
                  </td>
                  <td>
                    {teams.length === 0
                      ? <span style={{color:"var(--muted)"}}>—</span>
                      : (
                        <div className="tagRow">
                          {teams.map(t => (
                            <span key={t.id} className={`teamTag teamTag--${t.sport}`}>{t.name}</span>
                          ))}
                        </div>
                      )}
                  </td>
                  <td>
                    <span className={`statusTag statusTag--${u.active ? "active" : "inactive"}`}>
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ display:"flex", gap:6 }}>
                    {profileTeam && (
                      <button
                        className="dark"
                        onClick={() => setProfileFor({ user: u, team: profileTeam })}
                        title={`View cricket stats (${profileTeam.name})`}
                      >
                        <BarChart3 size={14}/>
                      </button>
                    )}
                    {canUpdate && (
                      <button className="dark" onClick={() => openEdit(u)} title="Edit">
                        <Pencil size={14}/>
                      </button>
                    )}
                    {canDelete && u.active && (
                      <button
                        className="dark"
                        onClick={() => remove(u)}
                        title="Deactivate"
                        style={{color:"var(--red)", borderColor:"rgba(255,107,107,0.4)"}}
                      >
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>

      {profileFor && (
        <PlayerProfile
          user={profileFor.user}
          team={profileFor.team}
          currentUser={user}
          onClose={() => setProfileFor(null)}
        />
      )}
    </div>
  );
}
